import React, { useRef, useState, useEffect } from "react";
import { Send } from "lucide-react";

const useInView = (options = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        observer.unobserve(entry.target);
      }
    }, { threshold: 0.15, ...options });

    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return [ref, isVisible];
};

export const ContactSection = () => {
  const [sectionRef, sectionVisible] = useInView();
  const [submitted, setSubmitted] = useState(false);

  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    const formData = new FormData(e.target);
    const data = {
      name: formData.get("name"),
      email: formData.get("email"),
      message: formData.get("message"),
      timestamp: new Date().toISOString(),
    };

    try {
      await fetch("https://hook.us2.make.com/1biqrmmwb9s568lslof3pcuoaxokjkcd", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      });
    } catch (err) {
      // Still show success to user even if webhook fails
    }

    setSubmitting(false);
    setSubmitted(true);
  };

  return (
    <section id="contact" style={{ background: "#ffffff" }}>
      <div
        ref={sectionRef}
        className="max-w-[87.5rem] mx-auto px-5 py-16 md:px-10 md:py-28"
        style={{ borderTop: "1px solid #e5e7eb" }}
      >
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-24">
          {/* Left — Heading */}
          <div
            style={{
              opacity: sectionVisible ? 1 : 0,
              transform: sectionVisible ? "translateY(0)" : "translateY(40px)",
              transition: "opacity 0.7s ease-out, transform 0.7s ease-out",
            }}
          >
            <span
              className="inline-block text-[#6b7280] text-xs font-medium uppercase tracking-[0.2em] mb-6"
              style={{ fontFamily: "'Inter', Arial, sans-serif" }}
            >
              Get In Touch
            </span>
            <h2
              className="text-[#FE3A80] uppercase leading-[0.85] mb-6 md:mb-8"
              style={{
                fontFamily: "'Montserrat', Arial, sans-serif",
                fontWeight: 900,
                fontSize: "clamp(2rem, 1.5rem + 2vw, 3rem)",
              }}
            >
              Contact Us
            </h2>
            <p
              className="text-[#1f2937] text-base md:text-lg font-medium leading-relaxed mb-6"
              style={{ fontFamily: "'Inter', Arial, sans-serif" }}
            >
              Have questions about sponsorship, partnerships, or the event? We'd love to hear from you.
            </p>
            <p
              className="text-[#6b7280] text-sm font-medium leading-relaxed"
              style={{ fontFamily: "'Inter', Arial, sans-serif" }}
            >
              You can also reach us directly at{" "}
              <a
                href="mailto:home@walkerottawa.com"
                className="text-[#FE3A80] no-underline hover:underline"
              >
                home@walkerottawa.com
              </a>
            </p>
          </div>

          {/* Right — Form */}
          <div
            className="flex flex-col justify-center"
            style={{
              opacity: sectionVisible ? 1 : 0,
              transform: sectionVisible ? "translateY(0)" : "translateY(40px)",
              transition: "opacity 0.7s ease-out 0.2s, transform 0.7s ease-out 0.2s",
            }}
          >
            {submitted ? (
              <div
                className="text-center py-12 px-6"
                style={{
                  background: "#fef2f6",
                  border: "1px solid #fecad9",
                }}
              >
                <p
                  className="text-[#FE3A80] text-lg font-semibold uppercase mb-2"
                  style={{ fontFamily: "'Montserrat', Arial, sans-serif" }}
                >
                  Thank You
                </p>
                <p
                  className="text-[#6b7280] text-sm font-medium"
                  style={{ fontFamily: "'Inter', Arial, sans-serif" }}
                >
                  We'll be in touch soon.
                </p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div>
                  <label
                    className="block text-[#6b7280] text-xs font-medium uppercase tracking-[0.15em] mb-2"
                    style={{ fontFamily: "'Inter', Arial, sans-serif" }}
                  >
                    Name
                  </label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-4 py-3 text-sm font-medium outline-none transition-all duration-300"
                    style={{
                      fontFamily: "'Inter', Arial, sans-serif",
                      color: "#1f2937",
                      background: "#ffffff",
                      border: "1px solid #e5e7eb",
                    }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = "#FE3A80"; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = "#e5e7eb"; }}
                  />
                </div>
                <div>
                  <label
                    className="block text-[#6b7280] text-xs font-medium uppercase tracking-[0.15em] mb-2"
                    style={{ fontFamily: "'Inter', Arial, sans-serif" }}
                  >
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    className="w-full px-4 py-3 text-sm font-medium outline-none transition-all duration-300"
                    style={{
                      fontFamily: "'Inter', Arial, sans-serif",
                      color: "#1f2937",
                      background: "#ffffff",
                      border: "1px solid #e5e7eb",
                    }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = "#FE3A80"; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = "#e5e7eb"; }}
                  />
                </div>
                <div>
                  <label
                    className="block text-[#6b7280] text-xs font-medium uppercase tracking-[0.15em] mb-2"
                    style={{ fontFamily: "'Inter', Arial, sans-serif" }}
                  >
                    Message
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={4}
                    className="w-full px-4 py-3 text-sm font-medium outline-none transition-all duration-300 resize-none"
                    style={{
                      fontFamily: "'Inter', Arial, sans-serif",
                      color: "#1f2937",
                      background: "#ffffff",
                      border: "1px solid #e5e7eb",
                    }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = "#FE3A80"; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = "#e5e7eb"; }}
                  />
                </div>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center justify-center gap-2 px-8 py-3.5 text-sm font-semibold uppercase cursor-pointer transition-all duration-300 hover:scale-[1.03] active:scale-[0.98] disabled:opacity-60 disabled:cursor-not-allowed"
                  style={{
                    fontFamily: "'Montserrat', Arial, sans-serif",
                    letterSpacing: "0.1em",
                    color: "#ffffff",
                    background: "#FE3A80",
                    border: "none",
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = "#ff5a96"; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = "#FE3A80"; }}
                >
                  {submitting ? "Sending..." : "Send Message"}
                  {!submitting && <Send size={14} />}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
